import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BadgeData, FilterOptions, TableFilterProps } from '@ui/v2/TableFilter/TableFilter'
import { useActiveSearchableFilterItems } from '@ui/v2/TableFilter/hooks/useActiveSearchableFilterItems'

import { extractValueFromFilterQuery } from '@utils/extractValueFromFilterQuery'

import { FilterFields, FilterQueryProps, FilterQueryPropsValue } from '@model/FilterFields'
import { NameDto, isName } from '@model/NameDto'
import {
  SubscriptionClassificationTypes,
  SubscriptionLevels,
  SubscriptionRequestTypes,
  SubscriptionStatuses,
  SubscriptionTypes,
} from '@model/SubscriptionsDto'

export const useFilterHandlers = (props: TableFilterProps) => {
  const { t } = useTranslation()
  const {
    setSearchFilters,
    filterOptions,
    searchFilters,
    countryOptions,
    entityOptions,
    currencyOptions,
    walletCurrencyOptions,
    statusOptions,
    rebatePeriods,
    sectionOptions,
    formatOptions,
    tradingAccountTypeOptions,
    showRebateCheckbox,
    groupSearchProps,
    tradingAccountSearchProps,
    clientsSearchProps,
    approvalTypes,
    wallets,
    types,
    categoriesSearchProps,
    sizesSearchProps,
    languagesSearchProps,
    campaignSearchProps,
    tradingAccountOptions,
  } = props

  const [scrollBehavior, setScrollBehavior] = useState<'reset' | 'preserve'>('reset')

  const {
    activeSearchableFilterItems,
    onActiveSearchableFilterItemsChange,
    setActiveSearchableFilterItems,
  } = useActiveSearchableFilterItems({
    groupSearchProps,
    tradingAccountSearchProps,
    clientsSearchProps,
    categoriesSearchProps,
    sizesSearchProps,
    languagesSearchProps,
    campaignSearchProps,
    searchFilters,
  })

  const handleSetSearchFilters = (value?: FilterQueryProps) => {
    onActiveSearchableFilterItemsChange(value)
    setSearchFilters(value)
    setScrollBehavior('preserve')
  }

  const handleClear = () => {
    handleSetSearchFilters({})
    setActiveSearchableFilterItems(undefined)
    setScrollBehavior('reset')
  }

  const clearRange = (from: FilterFields, to: FilterFields) =>
    handleSetSearchFilters({
      ...searchFilters,
      [from]: undefined,
      [to]: undefined,
    })

  const clearMulti = (key: FilterOptions) =>
    handleSetSearchFilters({
      ...searchFilters,
      [key]: removeMultiFilterValue({ name: '', value: '', type: key }, searchFilters),
    })

  const clearSingle = (key: FilterOptions) =>
    handleSetSearchFilters({ ...searchFilters, [key]: undefined })

  const handleSingleFilterClear = (badgeData: BadgeData) => {
    switch (badgeData.type) {
      case FilterOptions.Country:
      case FilterOptions.Entity:
      case FilterOptions.ClientEntity:
      case FilterOptions.Name:
      case FilterOptions.HasTradingAccounts:
        return clearSingle(badgeData.type)

      case FilterOptions.RebateApprovalType:
      case FilterOptions.TradingAccountType:
      case FilterOptions.AccountType:
      case FilterOptions.RebatePeriod:
      case FilterOptions.IntroducingBrokerGroupId:
      case FilterOptions.RebateGroupId:
      case FilterOptions.IBRebateGroupId:
      case FilterOptions.TradingAccountId:
      case FilterOptions.TradingAccount:
      case FilterOptions.CampaignId:
      case FilterOptions.CategoryId:
      case FilterOptions.LanguageId:
      case FilterOptions.SizeId:
      case FilterOptions.ClientId:
      case FilterOptions.Status:
      case FilterOptions.Section:
      case FilterOptions.Format:
      case FilterOptions.State:
      case FilterOptions.Currency:
      case FilterOptions.WalletCurrencyId:
      case FilterOptions.TransactionType:
      case FilterOptions.TransactionStatus:
      case FilterOptions.Type:
      case FilterOptions.Wallet:
      case FilterOptions.SubscriptionType:
      case FilterOptions.SubscriptionLevel:
      case FilterOptions.SubscriptionClassificationType:
      case FilterOptions.SubscriptionStatus:
      case FilterOptions.SubscriptionRequestType:
      case FilterOptions.TradingAccountPlatformType:
        return clearMulti(badgeData.type)

      case FilterOptions.ClientVolume:
        return clearRange(FilterFields.QueryVolumeFrom, FilterFields.QueryVolumeTo)
      case FilterOptions.CAClientVolume:
        return clearRange(FilterFields.ClientVolumeFrom, FilterFields.ClientVolumeTo)
      case FilterOptions.Volume:
        return clearRange(FilterFields.ClientVolumeFrom, FilterFields.ClientVolumeTo)
      case FilterOptions.Amount:
        return clearRange(FilterFields.AmountFrom, FilterFields.AmountTo)
      case FilterOptions.ClientIdAmount:
      case FilterOptions.CACommission:
        return clearRange(FilterFields.AmountFrom, FilterFields.AmountTo)
      case FilterOptions.ClientAmount:
        return clearRange(FilterFields.AmountFrom, FilterFields.AmountTo)
      case FilterOptions.Commission:
        return clearRange(FilterFields.QueryAmountFrom, FilterFields.QueryAmountTo)
      case FilterOptions.TradingAccountCount:
        return clearRange(
          FilterFields.TradingAccountsCountFrom,
          FilterFields.TradingAccountsCountTo
        )
      case FilterOptions.Price:
        return clearRange(FilterFields.ClientPriceFrom, FilterFields.ClientPriceTo)
      case FilterOptions.VolumeFxm:
        return clearRange(FilterFields.VolumeFxmFrom, FilterFields.VolumeFxmTo)
      case FilterOptions.VolumeOther:
        return clearRange(FilterFields.VolumeOtherFrom, FilterFields.VolumeOtherTo)
      case FilterOptions.AccumulatedVolumeOther:
        return clearRange(
          FilterFields.AccumulatedVolumeOtherFrom,
          FilterFields.AccumulatedVolumeOtherTo
        )
      case FilterOptions.AccumulatedVolumeFxm:
        return clearRange(
          FilterFields.AccumulatedVolumeFxmFrom,
          FilterFields.AccumulatedVolumeFxmTo
        )
      case FilterOptions.TotalCommission:
        return clearRange(FilterFields.TotalCommissionFrom, FilterFields.TotalCommissionTo)
      case FilterOptions.AccumulatedCommission:
        return clearRange(
          FilterFields.AccumulatedCommissionFrom,
          FilterFields.AccumulatedCommissionTo
        )
      case FilterOptions.RebateAmount:
        if (showRebateCheckbox) {
          handleSetSearchFilters({
            ...searchFilters,
            [FilterFields.HasRebate]: undefined,
          })
        }
        return clearRange(FilterFields.RebateAmountFrom, FilterFields.RebateAmountTo)
      case FilterOptions.SignedUpDate:
        return clearRange(FilterFields.SignedUpDateFrom, FilterFields.SignedUpDateTo)
      case FilterOptions.RegistrationDate:
        return clearRange(
          FilterFields.ClientRegistrationDateFrom,
          FilterFields.ClientRegistrationDateTo
        )
      case FilterOptions.TradePeriod:
      case FilterOptions.Date:
        return clearRange(FilterFields.ClientDateFrom, FilterFields.ClientDateTo)
      case FilterOptions.TransactionDate:
        return clearRange(FilterFields.TransactionDateFrom, FilterFields.TransactionDateTo)
      case FilterOptions.StatusChangedAtDate:
        return clearRange(FilterFields.StatusChangedAtDateFrom, FilterFields.StatusChangedAtDateTo)
      case FilterOptions.CreatedDate:
        return clearRange(FilterFields.CreatedDateFrom, FilterFields.CreatedDateTo)
      case FilterOptions.Balance:
        return clearRange(FilterFields.BalanceFrom, FilterFields.BalanceTo)
      case FilterOptions.VolumeSince2019:
        return clearRange(FilterFields.VolumeSince2019From, FilterFields.VolumeSince2019To)
      case FilterOptions.VolumeCurrentMonth:
        return clearRange(FilterFields.VolumeCurrentMonthFrom, FilterFields.VolumeCurrentMonthTo)
      case FilterOptions.VolumePreviousMonth:
        return clearRange(FilterFields.VolumePreviousMonthFrom, FilterFields.VolumePreviousMonthTo)
      case FilterOptions.FxmVolume:
        return clearRange(FilterFields.FxmVolumeFrom, FilterFields.FxmVolumeTo)
      case FilterOptions.OtherVolume:
        return clearRange(FilterFields.OtherVolumeFrom, FilterFields.OtherVolumeTo)
      case FilterOptions.FxmCommission:
        return clearRange(FilterFields.FxmAmountFrom, FilterFields.FxmAmountTo)
      case FilterOptions.OtherCommission:
        return clearRange(FilterFields.OtherAmountFrom, FilterFields.OtherAmountTo)
      default:
        return
    }
  }

  const activeFilterItems = useMemo<BadgeData[]>(() => {
    if (!searchFilters) return []

    return filterOptions.reduce<BadgeData[]>((activeFilters, option) => {
      let filterData: BadgeData[] = []

      switch (option) {
        case FilterOptions.Country:
          filterData = getSingleSelectNameDtoFilter(option, searchFilters[option], countryOptions)
          break

        case FilterOptions.Entity:
        case FilterOptions.ClientEntity:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], entityOptions)
          break

        case FilterOptions.Currency:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], currencyOptions)
          break

        case FilterOptions.WalletCurrencyId:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            walletCurrencyOptions
          )
          break

        case FilterOptions.RebateApprovalType:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], approvalTypes)
          break

        case FilterOptions.TradingAccountType:
        case FilterOptions.AccountType:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            tradingAccountTypeOptions
          )
          break

        case FilterOptions.RebatePeriod:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], rebatePeriods)
          break

        case FilterOptions.Status:
        case FilterOptions.State:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], statusOptions)
          break

        case FilterOptions.Section:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], sectionOptions)
          break

        case FilterOptions.Format:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], formatOptions)
          break

        case FilterOptions.Type:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], types)
          break

        case FilterOptions.Wallet:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], wallets)
          break

        case FilterOptions.SubscriptionType:
          filterData = getMultiSelectNameDtoFilter(option, searchFilters[option], SubscriptionTypes)
          break

        case FilterOptions.SubscriptionLevel:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            SubscriptionLevels
          )
          break

        case FilterOptions.SubscriptionClassificationType:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            SubscriptionClassificationTypes
          )
          break

        case FilterOptions.SubscriptionStatus:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            SubscriptionStatuses
          )
          break

        case FilterOptions.SubscriptionRequestType:
          filterData = getMultiSelectNameDtoFilter(
            option,
            searchFilters[option],
            SubscriptionRequestTypes
          )
          break

        case FilterOptions.IntroducingBrokerGroupId:
        case FilterOptions.RebateGroupId:
        case FilterOptions.IBRebateGroupId:
        case FilterOptions.TradingAccountId:
        case FilterOptions.TradingAccount:
        case FilterOptions.CampaignId:
        case FilterOptions.CategoryId:
        case FilterOptions.LanguageId:
        case FilterOptions.SizeId:
        case FilterOptions.ClientId:
          filterData = optionsToBadges(activeSearchableFilterItems?.[option] || [], option)
          break

        case FilterOptions.Name: {
          const v = extractValueFromFilterQuery(searchFilters[FilterOptions.Name])
          if (v) filterData = [{ name: v, value: v, type: option }]
          break
        }

        case FilterOptions.HasTradingAccounts:
          filterData = getSingleSelectNameDtoFilter(
            option,
            searchFilters[option],
            tradingAccountOptions
          )
          break

        default:
          break
      }

      const range = (from: FilterFields, to: FilterFields) =>
        getClientVolumeFilter(option, searchFilters, { from, to })

      switch (option) {
        case FilterOptions.ClientVolume:
          filterData = filterData.concat(
            range(FilterFields.QueryVolumeFrom, FilterFields.QueryVolumeTo)
          )
          break
        case FilterOptions.CAClientVolume:
          filterData = filterData.concat(
            range(FilterFields.ClientVolumeFrom, FilterFields.ClientVolumeTo)
          )
          break
        case FilterOptions.Volume:
          filterData = filterData.concat(
            range(FilterFields.ClientVolumeFrom, FilterFields.ClientVolumeTo)
          )
          break
        case FilterOptions.Amount:
          filterData = filterData.concat(range(FilterFields.AmountFrom, FilterFields.AmountTo))
          break
        case FilterOptions.ClientAmount:
        case FilterOptions.ClientIdAmount:
        case FilterOptions.CACommission:
          filterData = filterData.concat(range(FilterFields.AmountFrom, FilterFields.AmountTo))
          break
        case FilterOptions.Commission:
          filterData = filterData.concat(
            range(FilterFields.QueryAmountFrom, FilterFields.QueryAmountTo)
          )
          break
        case FilterOptions.TradingAccountCount:
          filterData = filterData.concat(
            range(FilterFields.TradingAccountsCountFrom, FilterFields.TradingAccountsCountTo)
          )
          break
        case FilterOptions.Price:
          filterData = filterData.concat(
            range(FilterFields.ClientPriceFrom, FilterFields.ClientPriceTo)
          )
          break
        case FilterOptions.VolumeFxm:
          filterData = filterData.concat(
            range(FilterFields.VolumeFxmFrom, FilterFields.VolumeFxmTo)
          )
          break
        case FilterOptions.VolumeOther:
          filterData = filterData.concat(
            range(FilterFields.VolumeOtherFrom, FilterFields.VolumeOtherTo)
          )
          break
        case FilterOptions.AccumulatedVolumeOther:
          filterData = filterData.concat(
            range(FilterFields.AccumulatedVolumeOtherFrom, FilterFields.AccumulatedVolumeOtherTo)
          )
          break
        case FilterOptions.AccumulatedVolumeFxm:
          filterData = filterData.concat(
            range(FilterFields.AccumulatedVolumeFxmFrom, FilterFields.AccumulatedVolumeFxmTo)
          )
          break
        case FilterOptions.TotalCommission:
          filterData = filterData.concat(
            range(FilterFields.TotalCommissionFrom, FilterFields.TotalCommissionTo)
          )
          break
        case FilterOptions.AccumulatedCommission:
          filterData = filterData.concat(
            range(FilterFields.AccumulatedCommissionFrom, FilterFields.AccumulatedCommissionTo)
          )
          break
        case FilterOptions.RebateAmount: {
          const chips = range(FilterFields.RebateAmountFrom, FilterFields.RebateAmountTo)
          if (showRebateCheckbox && searchFilters[FilterFields.HasRebate] === 'false') {
            chips.push({ name: t('IB.RebatesPage.Rebate N/A'), value: 'false', type: option })
          }
          filterData = filterData.concat(chips)
          break
        }
        case FilterOptions.SignedUpDate:
          filterData = filterData.concat(
            range(FilterFields.SignedUpDateFrom, FilterFields.SignedUpDateTo)
          )
          break
        case FilterOptions.RegistrationDate:
          filterData = filterData.concat(
            range(FilterFields.ClientRegistrationDateFrom, FilterFields.ClientRegistrationDateTo)
          )
          break
        case FilterOptions.TradePeriod:
        case FilterOptions.Date:
          filterData = filterData.concat(
            range(FilterFields.ClientDateFrom, FilterFields.ClientDateTo)
          )
          break
        case FilterOptions.TransactionDate:
          filterData = filterData.concat(
            range(FilterFields.TransactionDateFrom, FilterFields.TransactionDateTo)
          )
          break
        case FilterOptions.StatusChangedAtDate:
          filterData = filterData.concat(
            range(FilterFields.StatusChangedAtDateFrom, FilterFields.StatusChangedAtDateTo)
          )
          break
        case FilterOptions.CreatedDate:
          filterData = filterData.concat(
            range(FilterFields.CreatedDateFrom, FilterFields.CreatedDateTo)
          )
          break
        case FilterOptions.Balance:
          filterData = filterData.concat(range(FilterFields.BalanceFrom, FilterFields.BalanceTo))
          break
        case FilterOptions.VolumeSince2019:
          filterData = filterData.concat(
            range(FilterFields.VolumeSince2019From, FilterFields.VolumeSince2019To)
          )
          break
        case FilterOptions.VolumeCurrentMonth:
          filterData = filterData.concat(
            range(FilterFields.VolumeCurrentMonthFrom, FilterFields.VolumeCurrentMonthTo)
          )
          break
        case FilterOptions.VolumePreviousMonth:
          filterData = filterData.concat(
            range(FilterFields.VolumePreviousMonthFrom, FilterFields.VolumePreviousMonthTo)
          )
          break
        case FilterOptions.FxmVolume:
          filterData = filterData.concat(
            range(FilterFields.FxmVolumeFrom, FilterFields.FxmVolumeTo)
          )
          break
        case FilterOptions.OtherVolume:
          filterData = filterData.concat(
            range(FilterFields.OtherVolumeFrom, FilterFields.OtherVolumeTo)
          )
          break
        case FilterOptions.FxmCommission:
          filterData = filterData.concat(
            range(FilterFields.FxmAmountFrom, FilterFields.FxmAmountTo)
          )
          break
        case FilterOptions.OtherCommission:
          filterData = filterData.concat(
            range(FilterFields.OtherAmountFrom, FilterFields.OtherAmountTo)
          )
          break
        default:
          break
      }

      return [...activeFilters, ...filterData]
    }, [])
  }, [
    searchFilters,
    filterOptions,
    countryOptions,
    entityOptions,
    currencyOptions,
    approvalTypes,
    types,
    wallets,
    tradingAccountTypeOptions,
    rebatePeriods,
    statusOptions,
    sectionOptions,
    formatOptions,
    activeSearchableFilterItems,
    showRebateCheckbox,
    t,
  ])

  return {
    activeFilterItems,
    scrollBehavior,
    handleSingleFilterClear,
    handleClear,
    handleSetSearchFilters,
  }
}

const removeMultiFilterValue = (badgeData: BadgeData, searchFilters?: FilterQueryProps) => {
  const values = getMultiFilterValue(badgeData, searchFilters)
  return values.length > 0 ? values.join(' ') : undefined
}

const isBadgeData = (value?: any): value is BadgeData => !!value?.type

const getMultiFilterValue = (
  badgeData: BadgeData | FilterOptions,
  searchFilters?: FilterQueryProps
) => {
  if (isBadgeData(badgeData)) {
    const filterValue = searchFilters?.[badgeData.type]
    if (!filterValue || typeof filterValue !== 'string') return []
    return filterValue.split(' ').filter((value) => value !== badgeData.value.toString())
  }
  const filterValue = searchFilters?.[badgeData]
  if (!filterValue || typeof filterValue !== 'string') return []
  return filterValue.split(' ').filter((value) => value !== badgeData.toString())
}

const getFromToLabel = (from?: string, to?: string): string => {
  if (from?.length && to?.length) return `${from} - ${to}`
  if (from?.length || to?.length) return `${from ?? to}`
  return ''
}

const getClientVolumeFilter = (
  option: FilterOptions,
  searchFilters: FilterQueryProps,
  keys: { from: FilterFields; to: FilterFields }
): BadgeData[] => {
  const label = getFromToLabel(
    extractValueFromFilterQuery(searchFilters[keys.from]),
    extractValueFromFilterQuery(searchFilters[keys.to])
  )
  if (!label.length) return []
  return [{ name: label, value: label, type: option }]
}

const optionsToBadges = (
  options: NameDto<string | number>[],
  filterType: FilterOptions
): BadgeData[] =>
  options.map((option) => ({
    name: option.name,
    value: option.id,
    type: filterType,
  }))

const getMultiSelectNameDtoFilter = (
  option: FilterOptions,
  filterValue: FilterQueryPropsValue,
  options?: (string | NameDto<string | number>)[]
): BadgeData[] => {
  if (!filterValue || typeof filterValue !== 'string') return []
  const filterValues = filterValue.trim().split(' ')
  const matchedOptions = options
    ? options
        .filter((item) =>
          typeof item === 'string'
            ? filterValues.includes(item)
            : filterValues.includes(item.id.toString())
        )
        .map((item) => (typeof item === 'string' ? { id: item, name: item } : item))
    : []
  if (!matchedOptions?.length) return []
  return optionsToBadges(matchedOptions, option)
}

const getSingleSelectNameDtoFilter = (
  option: FilterOptions,
  filterValue: FilterQueryPropsValue,
  options: (NameDto<string> | string)[] = []
): BadgeData[] => {
  if (!filterValue) return []
  const found = (options || []).find((item) =>
    typeof item === 'string' ? item === filterValue : item.id === filterValue
  )
  if (!found) return []
  if (isName(found)) {
    return [{ name: found.name, value: filterValue as string, type: option }]
  }
  return [{ name: found as string, value: filterValue as string, type: option }]
}
